import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, Bot, Loader2, BookOpen, Info, Focus, Tag, X, Plus, FlaskConical, Zap, ArrowDown, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { BoundingBoxEditor, BoundaryBox } from "../BoundingBoxEditor";
import { AIQueryLibrary } from "../../notifications/AIQueryLibrary";
import { WorkflowTestSection, TestResultData } from "./WorkflowTestSection";
import { toast } from "sonner";
import { useSimpleRole } from "@/hooks/use-simple-role";

interface BaseWorkflowPanelProps {
  device: any;
  deviceId: string;
  questionValue: string;
  onQuestionChange: (value: string) => void;
  onSaveQuery: () => void;
  onTemplateModel?: (model: string) => void;
  onGenerateQuery: () => void;
  onSaveBoundingBoxes: (boxes: BoundaryBox[]) => Promise<void>;
  isUpdating: boolean;
  canEdit: boolean;
  notification: {result: string;reason: string;message: string;subject: string;};
  updateField?: (field: string, value: string | number) => Promise<void>;
  hideROI?: boolean;
  maskMode?: boolean;
  readOnly?: boolean;
  queryTitle?: string;
  queryIcon?: React.ElementType;
  roiTitle?: string;
  aiWorkflow?: string;
  enhanceButtonLabel?: string;
  enhanceButtonSubtitle?: string;
  enhanceInfoAlert?: React.ReactNode;
  showAoiShortcut?: boolean;
  topSection?: React.ReactNode;
  organizationId?: string | null;
}

export const BaseWorkflowPanel = ({
  device,
  deviceId,
  questionValue,
  onQuestionChange,
  onSaveQuery,
  onTemplateModel,
  onGenerateQuery,
  onSaveBoundingBoxes,
  isUpdating,
  canEdit,
  notification,
  updateField,
  hideROI,
  maskMode,
  readOnly,
  queryTitle,
  queryIcon,
  roiTitle,
  aiWorkflow,
  enhanceButtonLabel,
  enhanceButtonSubtitle,
  enhanceInfoAlert,
  showAoiShortcut,
  topSection,
  organizationId
}: BaseWorkflowPanelProps) => {
  const QueryIcon = queryIcon || Zap;
  const [isQueryLibraryOpen, setIsQueryLibraryOpen] = useState(false);
  const [addRequest, setAddRequest] = useState<{ nonce: number; query: string; dataTags: string[]; workflow?: string; model?: string | null } | null>(null);
  const { role } = useSimpleRole();
  const canAddToLibrary = role === 'superuser' || role === 'admin';
  const [tags, setTags] = useState<string[]>(() => {
    const stored = device?.notification_data_tags;
    if (Array.isArray(stored)) return stored;
    if (typeof stored === "string") {
      try {return JSON.parse(stored);} catch {return [];}
    }
    return [];
  });
  const [newTag, setNewTag] = useState("");
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [testResult, setTestResult] = useState<TestResultData | null>(null);
  const [tagsOpen, setTagsOpen] = useState(tags.length > 0);
  const [roiOpen, setRoiOpen] = useState(() => {
    const boxes = device?.boundary_boxes;
    return Array.isArray(boxes) && boxes.length > 0;
  });

  const scrollToRoi = () => {
    setRoiOpen(true);
    requestAnimationFrame(() => document.getElementById('roi-section')?.scrollIntoView({ behavior: 'smooth' }));
  };

  const saveTags = async (updatedTags: string[]) => {
    if (!updateField) return;
    setIsSavingTags(true);
    try {
      await updateField("notificationDataTags", JSON.stringify(updatedTags));
      toast.success("Data tags saved successfully");
    } catch {
      toast.error("Failed to save data tags");
    } finally {
      setIsSavingTags(false);
    }
  };

  const handleAddTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed || tags.length >= 5 || tags.includes(trimmed)) return;
    const updatedTags = [...tags, trimmed];
    setTags(updatedTags);
    setNewTag("");
    saveTags(updatedTags);
  };

  const handleRemoveTag = (index: number) => {
    const updatedTags = tags.filter((_, i) => i !== index);
    setTags(updatedTags);
    saveTags(updatedTags);
  };

  return (
    <div className="space-y-4">
      {topSection}
      {/* AI Query Section */}
      <Card className="border bg-muted/50">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <QueryIcon className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">{queryTitle || 'AI Query'}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {showAoiShortcut && !hideROI && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => scrollToRoi()}
                >
                  <ArrowDown className="h-3 w-3 mr-1" />
                  Draw AoI
                </Button>
              )}
              {canAddToLibrary && !readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={!questionValue?.trim()}
                  onClick={() => setAddRequest({ nonce: Date.now(), query: questionValue, dataTags: tags, workflow: aiWorkflow || device?.ai_workflow, model: device?.notification_ai_model ?? null })}>
                  <Plus className="h-3 w-3 mr-1" />
                  Save to Library
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-black"
                style={{ backgroundColor: '#DFEF97' }}
                disabled={!canEdit}
                onClick={() => setIsQueryLibraryOpen(true)}>
                <BookOpen className="h-4 w-4 mr-1" />
                Query Library
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="py-3">
          {readOnly && (
            <Alert className="mb-3">
              <Info className="h-4 w-4" />
              <AlertDescription>
                This AI Workflow may only be used with pre-trained custom AI models. Please select a Query from the Library to continue.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Textarea
              value={questionValue}
              onChange={(e) => onQuestionChange(e.target.value)}
              disabled={isUpdating || !canEdit || readOnly}
              className="min-h-[240px] resize-none"
              placeholder="Enter a true or false question" />
            
            {!readOnly && (
              <TooltipProvider>
                <div className="flex flex-col gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" onClick={onGenerateQuery} disabled={isUpdating || !canEdit} className="bg-component-background text-foreground border border-foreground flex flex-col h-auto py-2 px-3">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> :
                        <>
                            <div className="flex gap-1">
                              <Save className="h-4 w-4" />
                              <Bot className="h-4 w-4" />
                            </div>
                            <span className="text-[10px] mt-1 text-center leading-tight">{enhanceButtonLabel || 'AI Enhance'}</span>
                            {enhanceButtonSubtitle && <span className="text-[8px] text-muted-foreground text-center leading-tight">{enhanceButtonSubtitle}</span>}
                          </>
                        }
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Generate and save query using AI</p></TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" onClick={onSaveQuery} disabled={isUpdating || !canEdit} className="bg-component-background text-foreground border border-foreground flex flex-col h-auto py-2 px-3">
                        <Save className="h-4 w-4" />
                        <span className="text-[10px] mt-1">Save</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Save query</p></TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            )}
          </div>
          {!readOnly && <p className="text-xs text-muted-foreground mt-1">Choose from our library or write your own custom AI query for image analysis.</p>}
          {enhanceInfoAlert && !readOnly && <div className="mt-3">{enhanceInfoAlert}</div>}
        </CardContent>
      </Card>

      {/* Data Tags Section */}
      <Card className="border bg-muted/50">
        <Collapsible open={tagsOpen} onOpenChange={setTagsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="py-3 cursor-pointer select-none hover:bg-accent/30 transition-colors">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">Data Tags</CardTitle>
                <span className="text-xs text-muted-foreground">({tags.length}/5)</span>
                <ChevronDown className={cn("h-4 w-4 ml-auto text-muted-foreground transition-transform", tagsOpen && "rotate-180")} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
        <CardContent className="py-3">
          <p className="text-xs text-muted-foreground mb-3">
            Add up to 5 data tags to request structured data fields in the AI response output.
          </p>
          {!readOnly && (
            <div className="flex gap-2 mb-3">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                placeholder="Enter tag name"
                disabled={isUpdating || !canEdit || tags.length >= 5}
                className="flex-1" />

              <Button
                variant="outline"
                size="sm"
                onClick={handleAddTag}
                disabled={isUpdating || !canEdit || tags.length >= 5 || !newTag.trim()}
                className="bg-component-background text-foreground border border-foreground">

                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          )}
          {tags.length > 0 &&
          <div className="flex flex-wrap gap-2 mb-3">
              {tags.map((tag, i) =>
            <Badge key={i} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                  {tag}
                  {canEdit && !readOnly &&
              <button onClick={() => handleRemoveTag(i)} className="ml-1 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
              }
                </Badge>
            )}
            </div>
          }
          {isSavingTags &&
          <div className="flex justify-end">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving...
              </span>
            </div>
          }
        </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* ROI Section */}
      {!hideROI && <Card id="roi-section" className="border bg-muted/50">
        <Collapsible open={roiOpen} onOpenChange={setRoiOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="py-3 cursor-pointer select-none hover:bg-accent/30 transition-colors">
              <div className="flex items-center gap-2">
                <Focus className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">{roiTitle || 'Crop Image'}</CardTitle>
                <ChevronDown className={cn("h-4 w-4 ml-auto text-muted-foreground transition-transform", roiOpen && "rotate-180")} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
        <CardContent className="py-3">
          <BoundingBoxEditor
            deviceId={deviceId}
            imageUrl={device?.imageKit?.watermarkFreeUrl || device?.imageKit?.url}
            initialBoxes={device?.boundary_boxes as BoundaryBox[] || []}
            onSave={onSaveBoundingBoxes}
            disabled={isUpdating || !canEdit}
            maskMode={maskMode} />
        </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>}

      {/* Test Section */}
      <WorkflowTestSection deviceId={deviceId} device={device} disabled={isUpdating || !canEdit} />


      {/* Query Library Dialog */}
      <AIQueryLibrary
        isOpen={isQueryLibraryOpen}
        onClose={() => setIsQueryLibraryOpen(false)}
        aiWorkflow={aiWorkflow || device?.ai_workflow}
        organizationId={organizationId}
        addRequest={addRequest}
        onSelectQuery={(query, dataTags, model) => {
          if (model) onTemplateModel?.(model);
          onQuestionChange(query);
          if (updateField) {
            updateField("notificationQuestion", query);
          }
          if (dataTags && dataTags.length > 0 && updateField) {
            setTags(dataTags);
            saveTags(dataTags);
          }
        }} />
      
    </div>);

};